<template>
    <v-row  id="facility__location-details">
        <v-col cols="12">
            <h2 class="mt-4">Location Details</h2>
        </v-col>
        <v-col cols="12" sm="4">
            <app-input
                data-testid="facility-details-address-input"
                class="facility-details-address-input"
                :type="'text'"
                v-model.trim="currentFacility.street_address"
                label="Street Address"
                hint="Please enter the full street address, including Suite or Unit number (if applicable)"
                :rules="{ required: true }"
                :useValidation="true"
            />
        </v-col>
        <v-col cols="12" sm="4">
            <app-input
                data-testid="facility-details-city-input"
                class="facility-details-city-input"
                :type="'text'"
                v-model.trim="currentFacility.city"
                label="City"
                :rules="{ required: true }"
                :useValidation="true"
            />
        </v-col>
        <v-col cols="6" sm="2">
            <ValidationProvider
                rules="required"
                mode="eager"
                name="state"
                v-slot="{ valid, errors, invalid, classes }"
            >
                <v-select
                    data-testid="facility-details-state-select"
                    class="facility-details-state-select"
                    filled
                    v-model.trim="currentFacility.state"
                    :items="states"
                    label="State"
                    clearable
                    :class="classes"
                    :error-messages="errors.length ? 'This field is required' : ''"
                />
            </ValidationProvider>
        </v-col>
        <v-col cols="6" sm="2">
            <app-input
                data-testid="facility-details-zip-code-input"
                class="facility-details-zip-code-input"
                :type="'zipCode'"
                v-model.trim="currentFacility.postal_code"
                label="Zip Code"
                :rules="{ required: true, digits: 5 }"
                :maxLength="5"
                :useValidation="true"
            />
        </v-col>
        <v-col cols="12" sm="4">
            <app-input
                data-testid="facility-details-phone-input"
                class="facility-details-phone-input"
                :type="'phone'"
                v-model="currentFacility.phone"
                label="Phone Number"
                maxLength="12"
                :rules="{ required: true, phone: 12 }"
                :useValidation="true"
            />
        </v-col>
        <v-col cols="12" sm="4">
            <app-input
                data-testid="facility-details-email-input"
                class="facility-details-email-input"
                :type="'email'"
                v-model.trim="currentFacility.contact_email"
                label="Contact Email"
                hint="This is the email that will receive contact form submissions made on the
                      facility details page of the website. This email address may also be displayed to the public."
                :rules="{ required: true, email: true }"
                :useValidation="true"
            />
        </v-col>
        <v-col cols="12" sm="4">
            <app-input
                data-testid="facility-details-email-cc-input"
                class="facility-details-email-cc-input"
                :type="'email'"
                v-model.trim="currentFacility.contact_email_cc"
                label="Contact Email CC"
                hint="Enter one or multiple email addresses, separated by a comma.
                      These addresses will receive a copy of the contact form submissions made
                      on the facility details page of the website"
                :rules="{ required: true, email: true }"
                :useValidation="true"
           />
        </v-col>
        <v-col cols="12" sm="4">
            <app-input
                data-testid="facility-details-reservation-email-input"
                class="facility-details-reservation-email-input"
                :type="'email'"
                v-model.trim="currentFacility.reservation_email"
                label="Reservation Email(s)"
                hint="Enter one or multiple email addresses, separated by a comma."
                :rules="{ required: true }"
                :useValidation="true"
            />
        </v-col>
        <v-col cols="12" sm="4">
            <ValidationProvider
                rules="required"
                mode="eager"
                name="timezone"
                v-slot="{ valid, errors, invalid, classes }"
            >
                <v-select
                    data-testid="facility-details-time-zone-select"
                    class="facility-details-time-zone-select"
                    filled
                    item-text="text"
                    item-value="value"
                    v-model.trim="currentFacility.time_zone"
                    label="Time Zone"
                    :items="timezones"
                    required
                    clearable
                    hint="Select the facility's time zone."
                    :class="classes"
                    :error-messages="errors.length ? 'This field is required' : ''"
                />
            </ValidationProvider>
        </v-col>
        <v-col cols="12" v-if="showMap">
            <h2>Location Offset</h2>
            <p>
                If the map marker (pin) for the facility is not correct, simply zoom in on the map and click the desired
                location for the marker (pin). Do not try to drag the pin, simply click on the desired location and
                the pin will automatically adjust to the correct location.
            </p>
        </v-col>
        <v-col cols="12" v-if="showMap">
            <googlemaps-map
                    class="map"
                    :center="center"
                    :options="mapOptions"
                    :zoom="16"
                    @click="handleClick"
            >
                <googlemaps-marker
                        v-if="currentFacility.latitude && currentFacility.longitude"
                        title="Searched Location"
                        :position="searchLocation || { lat: currentFacility.latitude, lng: currentFacility.longitude }"
                />
            </googlemaps-map>

      <googlemaps-geocoder
        ref="results"
        :request="geocoderRequest"
      />
    </v-col>
  </v-row>
</template>

<script>
import AppInput from "@/components/global/AppInput";
import States from "@/helpers/states";
import { ValidationProvider } from "vee-validate";
import { timezones } from "@/helpers/facility/facility-timezone-list";

export default {
  props: {
    showMap: Boolean
  },

  data() {
    return {
      states: States,

      searchLocation: null,

      mapOptions: {
        clickableIcons: false
      },

      timezones: timezones
    };
  },

  computed: {
    currentFacility: {
      get() {
        return this.$store.state.facility.currentFacility;
      },
      set(newValue) {
        this.$store.commit("setCurrentFacilityData", newValue);
      }
    },

    center() {
      return {
        lat: this.$store.state.facility.currentFacility.latitude,
        lng: this.$store.state.facility.currentFacility.longitude
      };
    },

    geocoderRequest() {
      if (this.searchLocation) {
        return {
          location: this.searchLocation
        };
      } else {
        return null;
      }
    }
  },

  methods: {
    handleClick({ latLng }) {
      this.searchLocation = latLng;
      this.$store.commit("setCurrentFacilityLatLng", {
        lat: latLng.lat(),
        lng: latLng.lng()
      });
    }
  },

  components: {
    AppInput,
    ValidationProvider
  }
};
</script>

<style scoped>
    .map {
        min-height: 400px;
    }
</style>
